import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMembers } from '../features/members/hooks/useMembers';
import { MemberCard } from '../features/members/components/MemberCard';
import { MembersFilters } from '../features/members/components/MembersFilters';
import { Users } from 'lucide-react';
import { Button } from '../components/ui/button';

const MembersPage: React.FC = () => {
  const { t } = useTranslation('common');
  const {
    members, loading, page, setPage, totalPages, totalMembersCount,
    searchTerm, handleSearch, filterRole, handleRoleFilterChange
  } = useMembers();

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6 lg:p-8 pb-12">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg shadow-lg text-white">
            <Users size={32} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {t('members.header.title')}
            </h1>
            <p className="text-gray-500 mt-1 text-lg">
              {t('members.header.subtitle')}
            </p>
          </div>
        </div>

        <MembersFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          filterRole={filterRole}
          onRoleChange={handleRoleFilterChange}
          totalMembers={totalMembersCount}
        />

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center text-gray-400 bg-white rounded-lg border border-dashed">
            <Users size={48} className="mb-4 opacity-20" />
            <p>{t('members.empty')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {members.map(member => (
                <MemberCard key={member.id} member={member} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center mt-8 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  {t('common.prev')}
                </Button>
                <span className="flex items-center px-4 font-medium text-gray-600">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  {t('common.next')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MembersPage;
